import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, input } from '@angular/core';
import { Observable, Subject, concat, of, timer } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { AppFacade } from 'ish-core/facades/app.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { PriceItem } from 'ish-core/models/price-item/price-item.model';
import { whenTruthy } from 'ish-core/utils/operators';

@Component({
  selector: 'custom-mini-basket',
  templateUrl: './custom-mini-basket.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomMiniBasketComponent implements OnInit, OnDestroy {
  basketAnimation$: Observable<string>;
  itemTotal$: Observable<PriceItem>;
  itemCount$: Observable<number>;
  basketLoading$: Observable<boolean>;

  isCollapsed = true;

  readonly view = input<'auto' | 'small' | 'full'>('auto');

  private basketError$: Observable<HttpError>;
  private destroy$ = new Subject<void>();

  constructor(
    private checkoutFacade: CheckoutFacade,
    private appFacade: AppFacade,
    private location: Location,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.itemCount$ = this.checkoutFacade.basketItemCount$;
    this.itemTotal$ = this.checkoutFacade.basketItemTotal$;
    this.basketError$ = this.checkoutFacade.basketError$;
    this.basketLoading$ = this.checkoutFacade.basketLoading$;

    this.basketError$
      .pipe(
        whenTruthy(),
        filter(() => this.location.path() !== '/basket'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.open());

    this.basketAnimation$ = this.checkoutFacade.basketChange$.pipe(
      filter(() => !this.location.path().startsWith('/basket')),
      switchMap(() => concat(of('mini-basket-animation'), timer(2500).pipe(map(() => ''))))
    );

    this.basketAnimation$.pipe(takeUntil(this.destroy$)).subscribe(animation => {
      if (animation) {
        this.stopScroll();
        this.open();
      } else {
        this.allowScroll();
        this.collapse();
      }
    });

    this.appFacade.routingInProgress$.pipe(whenTruthy(), takeUntil(this.destroy$)).subscribe(() => {
      this.allowScroll();
      this.collapse();
    });
  }

  /**
   * Toggle the collapse state of the mini basket programmatically.
   */
  toggleCollapse() {
    this.stopScroll();
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Collapse the mini basket programmatically.
   */
  collapse() {
    this.isCollapsed = true;
    this.cdRef.markForCheck();
  }

  /**
   * Open the mini basket programmatically.
   */
  open() {
    this.isCollapsed = false;
    this.cdRef.markForCheck();
  }

  stopScroll() {
    if (!SSR) {
      document.body.style.overflowY = 'hidden';
    }
  }

  allowScroll() {
    if (!SSR) {
      document.body.style.overflowY = 'auto';
    }
  }
}
